import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, type RemindPreflight } from '@/api/client'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { useToast } from '@/hooks/use-toast'

export interface RemindTarget {
  platform: string
  externalId: string
  guestName: string | null
}

interface RemindDialogProps {
  target: RemindTarget | null
  onOpenChange: (open: boolean) => void
  /** Called after a confirmed send so the page can refetch the digest. */
  onSent: () => void
}

function fmt(at: string | null) {
  if (!at) return ''
  const d = new Date(at)
  return Number.isNaN(d.getTime()) ? at : d.toLocaleString()
}

type ApiFailure = {
  status?: number
  body?: { code?: string; warnings?: string[]; blocked?: string[]; error?: string }
}

/**
 * "Send a reminder" with the whole thread in view (CAP-301). The operator
 * asked to confirm, every time, that no reminder already went out before
 * sending another: the preflight surfaces recorded sends and any host
 * message after checkout that mentions a review, and the send needs an
 * explicit "send anyway" when either is present. Hard blocks (skipped,
 * expired, guest already reviewed, review in) disable the send entirely.
 * A refused send (409 / 422) refetches the preflight so what the dialog
 * shows is the server's current view, not the one from open time.
 *
 * The body is keyed by the target, so every open starts with fresh state
 * (a new idempotency key, no edits, no override) without any effect-driven
 * resets.
 */
export function RemindDialog({ target, onOpenChange, onSent }: RemindDialogProps) {
  return (
    <Dialog open={target !== null} onOpenChange={onOpenChange}>
      {target && (
        <RemindDialogBody
          key={`${target.platform}:${target.externalId}`}
          target={target}
          onOpenChange={onOpenChange}
          onSent={onSent}
        />
      )}
    </Dialog>
  )
}

function RemindDialogBody({
  target,
  onOpenChange,
  onSent,
}: {
  target: RemindTarget
  onOpenChange: (open: boolean) => void
  onSent: () => void
}) {
  const { toast } = useToast()
  const preflight = useQuery({
    queryKey: ['review-chase-remind-preflight', target.platform, target.externalId],
    queryFn: () => api.getRemindPreflight(target.platform, target.externalId),
    staleTime: 0,
  })
  // null = untouched: the textarea shows the server's draft until the
  // operator edits, after which their text wins even across a refetch.
  const [edited, setEdited] = useState<string | null>(null)
  const [override, setOverride] = useState(false)
  const [sending, setSending] = useState(false)
  // One key per open (the body remounts per target): a retry within the
  // same open replays instead of sending twice.
  const [idempotencyKey] = useState(() => crypto.randomUUID())

  const data: RemindPreflight | undefined = preflight.data
  const message = edited ?? data?.draft ?? ''
  const blocked = data?.blocked ?? []
  const warnings = data?.warnings ?? []
  const canSend = !!data && blocked.length === 0 && message.trim().length > 0 && !sending
  const needsOverride = warnings.length > 0 && !override

  const send = async () => {
    if (!canSend) return
    setSending(true)
    try {
      const res = await api.sendReminder(target.platform, target.externalId, {
        message: message.trim(),
        idempotencyKey,
        override,
      })
      toast({
        title: res.replayed ? 'Reminder already sent' : 'Reminder sent',
        description: `${target.guestName || 'The guest'} · ${res.reminder_sends.length} reminder${res.reminder_sends.length === 1 ? '' : 's'} on record`,
      })
      onSent()
      onOpenChange(false)
    } catch (err) {
      const e = err as ApiFailure
      if (e.status === 409 || e.status === 422) {
        // The server saw something this dialog did not (a reminder that
        // just went out, a review that just arrived). Show its view.
        setOverride(false)
        await preflight.refetch()
        toast({
          title: e.status === 409 ? 'Already reminded' : 'Cannot send',
          description:
            e.status === 409
              ? 'Read the updated thread below, then tick "Send anyway" if you still want to.'
              : (e.body?.blocked ?? []).join(' ') || 'This stay can no longer be reminded.',
          variant: e.status === 409 ? undefined : 'destructive',
        })
      } else {
        toast({
          title: 'Send failed',
          description: e.body?.error || 'The reminder was not sent. Try again in a moment.',
          variant: 'destructive',
        })
      }
    } finally {
      setSending(false)
    }
  }

  return (
    <DialogContent
      className="max-w-2xl"
      onEscapeKeyDown={e => sending && e.preventDefault()}
      onPointerDownOutside={e => sending && e.preventDefault()}
      onInteractOutside={e => sending && e.preventDefault()}
    >
      <DialogHeader>
        <DialogTitle>Remind {target.guestName || 'the guest'} to review</DialogTitle>
        <DialogDescription>
          The whole thread is below. Check nothing already asked for a review before sending
          another.
        </DialogDescription>
      </DialogHeader>

      {preflight.isLoading && <LoadingSkeleton className="h-40 w-full" />}
      {preflight.isError && (
        <p className="text-sm text-danger">Couldn&apos;t load the thread for this stay.</p>
      )}

      {data && (
        <div className="space-y-4">
          {blocked.length > 0 && (
            <div className="rounded-card border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">
              <div className="font-semibold">Cannot send</div>
              <ul className="list-disc pl-4">
                {blocked.map(b => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            </div>
          )}
          {warnings.length > 0 && (
            <div className="rounded-card border border-amber/30 bg-amber/10 px-3 py-2 text-sm text-amber">
              <div className="font-semibold">Already reminded?</div>
              <ul className="list-disc pl-4">
                {warnings.map(w => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
              {data.prior_reminder && (
                <blockquote className="mt-1 border-l-2 border-amber/50 pl-2 text-xs text-text">
                  “{data.prior_reminder.excerpt}”
                </blockquote>
              )}
              <label className="mt-2 flex items-center gap-2 text-xs text-text">
                <input
                  type="checkbox"
                  checked={override}
                  onChange={e => setOverride(e.target.checked)}
                />
                Send anyway, I have read the thread
              </label>
            </div>
          )}

          <div>
            <div className="mb-1 text-xs font-medium text-textMuted">
              Thread · {data.messages.length} message{data.messages.length === 1 ? '' : 's'} ·
              checkout {data.chase.checkout_date}
              {data.chase.host_reviewed_at ? ' · you reviewed' : ''}
            </div>
            <div
              className="max-h-72 space-y-2 overflow-y-auto rounded-card border border-border bg-surface2/40 p-3"
              role="log"
              aria-label="Guest thread"
            >
              {data.messages.length === 0 && (
                <p className="text-xs text-textMuted">No messages stored for this stay.</p>
              )}
              {data.messages.map((m, i) => (
                <div
                  key={m.id ?? i}
                  className={`flex ${m.direction === 'host_to_guest' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-lg px-3 py-2 text-xs ${
                      m.direction === 'host_to_guest'
                        ? 'bg-mint/15 text-text'
                        : 'bg-white/10 text-text'
                    }`}
                  >
                    <div className="mb-0.5 text-[10px] uppercase tracking-wide text-textMuted">
                      {m.direction === 'host_to_guest' ? 'You' : data.chase.guest_name || 'Guest'}
                      {m.at ? ` · ${fmt(m.at)}` : ''}
                    </div>
                    <div className="whitespace-pre-wrap">{m.body}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label
              htmlFor="remind-message"
              className="mb-1 block text-xs font-medium text-textMuted"
            >
              Message (
              {data.wording === 'soft'
                ? 'softened wording, per the confidence score'
                : 'standard wording'}
              )
            </label>
            <textarea
              id="remind-message"
              value={message}
              onChange={e => setEdited(e.target.value)}
              disabled={sending || blocked.length > 0}
              className="min-h-[110px] w-full resize-y rounded-input border border-border bg-surface2 px-3 py-2 text-sm text-text focus-visible:border-mint focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint"
            />
          </div>
        </div>
      )}

      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
          Cancel
        </Button>
        <Button onClick={send} disabled={!canSend || needsOverride}>
          {sending ? 'Sending…' : 'Send reminder'}
        </Button>
      </DialogFooter>
    </DialogContent>
  )
}
